/**
 * researchApi.js — the Research section's only data layer.
 * =============================================================================
 * Every Research component reads from here. No component fetches
 * /api/researcher/* or /api/research/* directly any more.
 *
 * Why a context and not React Query: the app has neither React Query nor SWR
 * installed, and adding a dependency to solve a two-endpoint caching problem is
 * not worth the bundle. This is the same contract in ~100 lines — one in-flight
 * request per endpoint, a TTL cache shared across tabs, and a refetch interval.
 *
 * The bug it fixes: the old page re-ran all nine always-on connections on EVERY
 * tab switch, because the slow-beat effect was keyed on `tab` and had no cache
 * between mounts. Here a tab switch is free — the data is already in the cache.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export const REFRESH_MS = 45000;   // §5.6 asks for 30-60s
const STALE_MS = 30000;            // serve from cache below this age

const ResearchDataContext = createContext(null);

async function fetchJson(path, signal) {
  const res = await fetch(`${API}${path}`, { signal });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

export function ResearchDataProvider({ children }) {
  // { [path]: { data, error, at, loading } }
  const [entries, setEntries] = useState({});
  const inflight = useRef(new Map());
  const mounted = useRef(true);
  // A ref mirror of the cache so `load` can read it without listing `entries`
  // as a dependency — which would re-create `load` on every state change and
  // restart every subscriber's interval.
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  useEffect(() => () => { mounted.current = false; }, []);

  const load = useCallback(async (path, { force = false } = {}) => {
    const existing = inflight.current.get(path);
    if (existing) return existing;               // one request per endpoint

    const cached = entriesRef.current[path];
    if (!force && cached?.at && Date.now() - cached.at < STALE_MS && !cached.error) {
      return cached.data;
    }

    const promise = (async () => {
      try {
        const data = await fetchJson(path);
        if (mounted.current) {
          setEntries((e) => ({ ...e, [path]: { data, error: null, at: Date.now(), loading: false } }));
        }
        return data;
      } catch (err) {
        if (mounted.current) {
          // Keep the last good data on screen; record the error beside it so the
          // UI can say the numbers are stale rather than silently showing old
          // values as if they were current.
          setEntries((e) => ({
            ...e,
            [path]: { data: e[path]?.data ?? null, error: err.message, at: Date.now(), loading: false },
          }));
        }
        return null;
      } finally {
        inflight.current.delete(path);
      }
    })();

    inflight.current.set(path, promise);
    setEntries((e) => ({ ...e, [path]: { ...(e[path] || {}), loading: true } }));
    return promise;
  }, []);

  const value = useMemo(() => ({ entries, load }), [entries, load]);
  return (
    <ResearchDataContext.Provider value={value}>
      {children}
    </ResearchDataContext.Provider>
  );
}

/** Subscribe to one endpoint. Shared cache, one interval per path. */
function useEndpoint(path, { refreshMs = REFRESH_MS, enabled = true } = {}) {
  const ctx = useContext(ResearchDataContext);
  if (!ctx) {
    throw new Error("useEndpoint must be used inside <ResearchDataProvider>");
  }
  const { entries, load } = ctx;

  useEffect(() => {
    if (!enabled || !path) return undefined;
    load(path);
    if (!refreshMs) return undefined;
    const t = setInterval(() => load(path, { force: true }), refreshMs);
    return () => clearInterval(t);
  }, [path, refreshMs, enabled, load]);

  const entry = entries[path] || {};
  return {
    data: entry.data ?? null,
    error: entry.error ?? null,
    // Only "loading" when there is nothing to show yet. A background refresh
    // must not blank a populated page.
    loading: !!entry.loading && !entry.data,
    refreshing: !!entry.loading && !!entry.data,
    updatedAt: entry.at ?? null,
    reload: useCallback(() => load(path, { force: true }), [load, path]),
  };
}

export function useResearchSummary(opts) {
  return useEndpoint("/research/summary", opts);
}

export function useStrategies(params = {}, opts) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== "") qs.set(k, v);
  });
  const suffix = qs.toString() ? `?${qs}` : "";
  return useEndpoint(`/research/strategies${suffix}`, opts);
}

export function useStrategy(key, opts) {
  return useEndpoint(key ? `/research/strategies/${encodeURIComponent(key)}` : null, opts);
}

/** Legacy endpoints the Inputs and Ops tabs still read, through the same cache. */
export function useLegacy(path, opts) {
  return useEndpoint(path, opts);
}
